import React, { useState, useEffect } from 'react';
import { TrendingUp } from 'lucide-react';
import type { LoanData } from './types';
import { formatCurrency, getInterestRateForInstallments, calculateLoanDetails } from './types';
import { calcDefaultInstallmentDates } from '../../lib/workdays';

interface Props {
  data: LoanData;
  onComplete: (data: LoanData) => void;
  statementCheck?: React.ReactNode;
}

export default function SimulationStep({ data, onComplete, statementCheck }: Props) {
  const [loan, setLoan] = useState<LoanData>(data);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const n = parseInt(loan.installments);
    if (n > 0) {
      const dates = calcDefaultInstallmentDates(n, loan.isCustom);
      setLoan(prev => ({ ...prev, installmentDates: dates }));
    }
  }, [loan.installments, loan.isCustom]);

  const handleInstallmentsChange = (value: string) => {
    if (value === 'custom') {
      setLoan(prev => ({ ...prev, isCustom: true, installments: '1', interestRate: '70' }));
    } else {
      setLoan(prev => ({
        ...prev,
        isCustom: false,
        installments: value,
        interestRate: getInterestRateForInstallments(value),
      }));
    }
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    const amount = parseFloat(loan.amount);
    if (!loan.amount || isNaN(amount) || amount <= 0) {
      errs.amount = 'Informe o valor do empréstimo';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleContinue = () => {
    if (!validate()) return;
    onComplete(loan);
  };

  const amount = parseFloat(loan.amount) || 0;
  const details = amount > 0 ? calculateLoanDetails(loan.amount, loan.installments, loan.interestRate) : null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Simulação do Empréstimo</h2>
        <p className="text-gray-600 text-sm">Configure os valores financeiros do contrato.</p>
      </div>

      {/* Valor */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Valor do Empréstimo (R$) *
        </label>
        <input
          type="number"
          value={loan.amount}
          onChange={(e) => setLoan(prev => ({ ...prev, amount: e.target.value }))}
          min="0"
          step="0.01"
          placeholder="0,00"
          className={`w-full px-4 py-3 border rounded-xl text-base transition-colors focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.amount ? 'border-red-400 bg-red-50' : 'border-gray-300'
          }`}
        />
        {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
      </div>

      {/* Parcelas */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Número de Parcelas
        </label>
        <select
          value={loan.isCustom ? 'custom' : loan.installments}
          onChange={(e) => handleInstallmentsChange(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="1">1 parcela (70%)</option>
          <option value="2">2 parcelas (80%)</option>
          <option value="3">3 parcelas (90%)</option>
          <option value="custom">Personalizada</option>
        </select>
      </div>

      {loan.isCustom && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Parcelas (1-10)
            </label>
            <input
              type="number"
              value={loan.installments}
              onChange={(e) => {
                const v = Math.min(Math.max(parseInt(e.target.value) || 1, 1), 10);
                setLoan(prev => ({ ...prev, installments: v.toString() }));
              }}
              min="1"
              max="10"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Taxa (10%-200%)
            </label>
            <input
              type="number"
              value={loan.interestRate}
              onChange={(e) => {
                const v = Math.min(Math.max(parseFloat(e.target.value) || 10, 10), 200);
                setLoan(prev => ({ ...prev, interestRate: v.toString() }));
              }}
              min="10"
              max="200"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      )}

      {/* Resultado */}
      {details && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-blue-600" />
            <h3 className="text-sm font-semibold text-gray-800">Resumo da Simulação</h3>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Valor solicitado</span>
              <span>{formatCurrency(amount)}</span>
            </div>
            <div className="flex justify-between text-red-600">
              <span>Taxa final descontada</span>
              <span>- {formatCurrency(details.discountFee)}</span>
            </div>
            <div className="flex justify-between font-semibold text-emerald-700">
              <span>Valor que o cliente receberá</span>
              <span>{formatCurrency(details.amountReceived)}</span>
            </div>

            <div className="border-t border-gray-200 pt-2 space-y-1.5">
              <div className="flex justify-between text-gray-600">
                <span>Valor + taxa inicial</span>
                <span>{formatCurrency(details.amountWithFee)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Valor/parcela sem juros</span>
                <span>{formatCurrency(details.baseInstallmentAmount)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Taxa de juros</span>
                <span>{loan.interestRate}%</span>
              </div>
              <div className="flex justify-between text-gray-700 font-medium">
                <span>Valor/parcela com juros</span>
                <span>{formatCurrency(details.installmentAmount)}</span>
              </div>
            </div>

            <div className="flex justify-between font-bold text-gray-900 text-base border-t border-gray-200 pt-2">
              <span>Total a pagar</span>
              <span>{formatCurrency(details.totalAmount)}</span>
            </div>
          </div>
        </div>
      )}

      {statementCheck}

      <button
        onClick={handleContinue}
        disabled={!amount}
        className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold text-base hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Continuar
      </button>
    </div>
  );
}
